import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "../../hooks/useConfirm";
import { User, Mail, Phone, Save, Eye, EyeOff, Fingerprint, Trash2, Smartphone, Loader2 } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { isBiometricAvailable, registerBiometric, getCredentials, deleteCredential } from "../../utils/webauthn";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profilo = () => {
  const { getAuthHeaders, user, token } = useAuth();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricCreds, setBiometricCreds] = useState([]);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [form, setForm] = useState({
    nome: "",
    email: "",
    telefono: "",
    password: "",
    password_confirm: ""
  });

  useEffect(() => {
    if (user) {
      setForm({
        nome: user.nome || "",
        email: user.email || "",
        telefono: user.telefono || "",
        password: "",
        password_confirm: ""
      });
    }
  }, [user]);

  const fetchBiometricCreds = useCallback(async () => {
    if (token) {
      const creds = await getCredentials(token);
      setBiometricCreds(creds);
    }
  }, [token]);

  useEffect(() => {
    isBiometricAvailable().then(setBiometricAvailable);
    fetchBiometricCreds();
  }, [fetchBiometricCreds]);

  const handleRegisterBiometric = async () => {
    setBiometricLoading(true);
    try {
      await registerBiometric(token);
      // Salva l'identificatore di login (email o telefono) per il biometric
      localStorage.setItem("biometric_username", user.email || user.telefono || user.username || "");
      toast.success("Accesso biometrico configurato!");
      fetchBiometricCreds();
    } catch (err) {
      if (err.name === "NotAllowedError") {
        toast.error("Registrazione annullata");
      } else {
        toast.error(err.message || "Errore configurazione biometria");
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  const handleDeleteBiometric = async (credId) => {
    if (!await confirm("Rimuovere questo accesso biometrico?", { confirmLabel: "Rimuovi" })) return;
    try {
      await deleteCredential(token, credId);
      toast.success("Credenziale rimossa");
      fetchBiometricCreds();
      const remaining = biometricCreds.filter(c => c.id !== credId);
      if (remaining.length === 0) localStorage.removeItem("biometric_username");
    } catch {
      toast.error("Errore rimozione");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (form.password && form.password !== form.password_confirm) {
      toast.error("Le password non coincidono");
      return;
    }

    setLoading(true);
    try {
      const updateData = {
        nome: form.nome,
        email: form.email,
        telefono: form.telefono
      };
      
      if (form.password) {
        updateData.password = form.password;
      }

      await axios.put(`${API}/utenti/${user.id}`, updateData, {
        headers: getAuthHeaders()
      });
      
      toast.success("Profilo aggiornato con successo");
      setForm(prev => ({ ...prev, password: "", password_confirm: "" }));
    } catch (error) {
      toast.error("Errore nell'aggiornamento del profilo");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container" data-testid="profilo-page">
      <div>
        <h1 className="page-title">Il mio Profilo</h1>
        <p className="page-subtitle">Gestisci i tuoi dati personali</p>
      </div>

      <div className="bg-card backdrop-blur-sm border border-border rounded-2xl p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-lime-400 rounded-full flex items-center justify-center">
            <User className="h-8 w-8 text-black" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">{user?.nome || user?.email || user?.telefono}</h2>
            <p className="text-muted-foreground capitalize">{user?.ruolo}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground">Nome Completo</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={form.nome}
                  onChange={(e) => setForm(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="Nome e Cognome"
                  className="pl-10 bg-muted border-border text-foreground rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value.toLowerCase().trim() }))}
                  placeholder="email@esempio.it"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode="email"
                  className="pl-10 bg-muted border-border text-foreground rounded-xl lowercase"
                  data-testid="profilo-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Telefono</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={form.telefono}
                  onChange={(e) => setForm(prev => ({ ...prev, telefono: e.target.value.replace(/\D/g, '') }))}
                  placeholder="3331234567"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="pl-10 bg-muted border-border text-foreground rounded-xl"
                  data-testid="profilo-telefono"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-4 mt-4">
            <h3 className="text-sm font-medium text-muted-foreground mb-3">Cambia Password</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Nuova Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="••••••••"
                    className="pr-10 bg-muted border-border text-foreground rounded-xl"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground">Conferma Password</Label>
                <Input
                  type={showPassword ? "text" : "password"}
                  value={form.password_confirm}
                  onChange={(e) => setForm(prev => ({ ...prev, password_confirm: e.target.value }))}
                  placeholder="••••••••"
                  className="bg-muted border-border text-foreground rounded-xl"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-lime-400 hover:bg-lime-300 text-black rounded-xl"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? "Salvataggio..." : "Salva Modifiche"}
            </Button>
          </div>
        </form>
      </div>

      {/* Biometric Authentication Section */}
      {biometricAvailable && (
        <div className="bg-card backdrop-blur-sm border border-border rounded-2xl p-6" data-testid="biometric-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#F0EFFE" }}>
              <Fingerprint className="h-5 w-5" style={{ color: "#6C63FF" }} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">Accesso Biometrico</h3>
              <p className="text-xs text-muted-foreground">Face ID, impronta digitale o Windows Hello</p>
            </div>
          </div>

          {biometricCreds.length > 0 ? (
            <div className="space-y-3 mb-4">
              {biometricCreds.map((cred) => (
                <div key={cred.id} className="flex items-center justify-between p-3 rounded-xl bg-muted border border-border" data-testid={`biometric-cred-${cred.id}`}>
                  <div className="flex items-center gap-3">
                    <Smartphone className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{cred.device_name}</p>
                      <p className="text-xs text-muted-foreground">
                        Registrato il {new Date(cred.created_at).toLocaleDateString("it-IT")}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost" size="icon"
                    onClick={() => handleDeleteBiometric(cred.id)}
                    className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                    data-testid={`delete-biometric-${cred.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground mb-4">
              Nessun accesso biometrico configurato. Configura il riconoscimento facciale o l'impronta digitale per accedere rapidamente senza password.
            </p>
          )}

          <Button
            onClick={handleRegisterBiometric}
            disabled={biometricLoading}
            className="rounded-xl text-white font-semibold"
            style={{ background: "linear-gradient(135deg, #6C63FF, #8B85FF)" }}
            data-testid="register-biometric-btn"
          >
            {biometricLoading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Configurazione...</>
            ) : (
              <><Fingerprint className="h-4 w-4 mr-2" /> {biometricCreds.length > 0 ? "Aggiungi altro dispositivo" : "Configura accesso biometrico"}</>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default Profilo;
